import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ExpiringClaims,
    fromExpiringTokenClaims,
    isDecodedToken,
    Permission,
    Resource
} from '@pepperhq/auth-client';
import { authApi } from 'components/auth/AuthApi';
import { Tenant } from 'components/merchant/models/Tenant';
import { tenantApi } from 'components/merchant/tenantApi';
import { doSignInWithCustomToken, doSignOut } from 'lib/firebase/auth';
import { auth } from 'lib/firebase/firebase';
import { httpClient } from 'lib/HttpClient';
import logger from 'lib/logger';
import type { AppDispatch } from '../store';

export interface FirebaseIdentifiers {
    email?: string;
    name?: string;
}

export interface AuthState {
    claims?: ExpiringClaims;
    authorizedDate?: Date;
    tenant?: Tenant;
    error?: string;
    identifiers: FirebaseIdentifiers;
    isAuthorizingInProgress: boolean;
    isLegacyLoggedIn?: boolean;
    isLegacyLoading?: boolean;
}

export const initialState: AuthState = {
    identifiers: { email: undefined, name: undefined },
    isAuthorizingInProgress: false,
    isLegacyLoading: true,
    isLegacyLoggedIn: false
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        signInStart(state) {
            state.error = null;
            state.isAuthorizingInProgress = false;
        },
        signInSuccess(state) {
            state.error = null;
            state.isAuthorizingInProgress = true;
        },
        getAccessSuccess(state) {
            state.error = null;
            state.isAuthorizingInProgress = false;
        },
        getAccessFail(state, action: PayloadAction<{ error: string }>) {
            state.error = action.payload.error;
            state.isAuthorizingInProgress = false;
        },
        signInFail(state, action: PayloadAction<{ error: string }>) {
            state.error = action.payload.error;
            state.isAuthorizingInProgress = false;
        },
        authorizeSuccess(
            state,
            action: PayloadAction<{
                claims: ExpiringClaims;
                tenant: Tenant;
                identifiers: FirebaseIdentifiers;
            }>
        ) {
            state.claims = action.payload.claims;
            state.tenant = action.payload.tenant;
            state.identifiers = action.payload.identifiers;
            state.isAuthorizingInProgress = false;
            state.authorizedDate = new Date();
        },
        authorizeFail(state, action: PayloadAction<{ error: string }>) {
            state.error = action.payload.error;
            state.claims = null;
            state.authorizedDate = undefined;
            state.isAuthorizingInProgress = false;
        },
        signOutSuccess(state) {
            state.claims = null;
            state.authorizedDate = undefined;
            state.isAuthorizingInProgress = false;
        },
        unauthorizeSuccess(state) {
            state.claims = null;
            state.authorizedDate = undefined;
            state.isAuthorizingInProgress = false;
        },
        legacyAuthorizeStart(state) {
            state.isLegacyLoading = true;
            state.isLegacyLoggedIn = false;
        },
        legacyAuthorizeError(state) {
            state.isLegacyLoading = false;
            state.isLegacyLoggedIn = false;
        },
        legacyAuthorizeSuccess(state) {
            state.isLegacyLoading = false;
            state.isLegacyLoggedIn = true;
        }
    }
});

export const {
    signInStart,
    signInSuccess: signInSuccessAction,
    getAccessSuccess: getAccessSuccessAction,
    getAccessFail: getAccessFailAction,
    signInFail: signInFailAction,
    authorizeSuccess: authorizeSuccessAction,
    authorizeFail: authorizeFailAction,
    signOutSuccess,
    unauthorizeSuccess,
    legacyAuthorizeStart,
    legacyAuthorizeError,
    legacyAuthorizeSuccess
} = authSlice.actions;

// --- Helpers ---

function decodeToken(token: any): ExpiringClaims {
    if (!token || !isDecodedToken(token)) {
        throw new Error('not a token');
    }
    return fromExpiringTokenClaims(token);
}

// --- Thunks ---

// We do not put user to store here because we do in authStateChanged listener (authorize action)
export const signIn = (email: string, password: string) => async (dispatch: AppDispatch) => {
    try {
        // Sign in Flow:
        // 1. POST {user}/tokens (email, password) -> custom token
        // 2. sign into firebase with custom token -> auth state changes
        // 3. auth state change listener triggers authorise action (below)
        // 4. POST {manager}/authorise (idtoken) -> stores token in session cookie
        // 5. idtoken is decoded into claims and triggers action authorizeSuccess
        dispatch(signInStart());
        const response = await authApi.signInWithEmailAndPassword(email, password);
        if (response.statusCode !== 200) {
            return dispatch(signInFailAction({ error: response.body.message }));
        }
        await doSignInWithCustomToken(response.body.token);
        dispatch(signInSuccessAction());
    } catch (error) {
        dispatch(signInFailAction({ error: error.message }));
    }
};

export const signOut = () => async (dispatch: AppDispatch) => {
    await doSignOut();
    httpClient.updateToken(null);
    dispatch(signOutSuccess());
    dispatch({ type: 'DESTROY_SESSION' });
};

export const authorize = () => async (dispatch: AppDispatch) => {
    try {
        const currentUser = auth().currentUser;
        const tokenResult = await currentUser.getIdTokenResult(true);
        const email = tokenResult.claims.email as string;
        const name = tokenResult.claims.name as string;
        httpClient.updateToken(tokenResult.token, new Date(tokenResult.expirationTime));
        const claims = decodeToken(tokenResult.claims);
        let tenant;
        if (claims.tenantId && claims.hasPermission(Resource.Tenant, Permission.read)) {
            const tenantResult = await tenantApi.get(claims.tenantId);
            if (tenantResult.ok) {
                tenant = tenantResult.body;
            } else if (tenantResult.statusCode === 404) {
                return unauthorize()(dispatch);
            }
        }
        dispatch(authorizeSuccessAction({ claims, tenant, identifiers: { email, name } }));
    } catch (error) {
        logger.error(error);
        dispatch(authorizeFailAction({ error: error.message }));
        signOut()(dispatch);
    }
};

export const unauthorize = () => (dispatch: AppDispatch) => {
    httpClient.updateToken(null);
    return dispatch(unauthorizeSuccess());
};

export const getAccess = (token: string) => async (dispatch: AppDispatch) => {
    try {
        await doSignInWithCustomToken(token);
        await authorize()(dispatch);
        dispatch({ type: 'DESTROY_SESSION' });
        dispatch(getAccessSuccessAction());
    } catch (error) {
        dispatch(getAccessFailAction({ error }));
        signOut()(dispatch);
    }
};

export default authSlice.reducer;
